import React, { useEffect, useState } from 'react';
import { ISideNavigationMenuItemsConfig, SIDE_MENU_URL_LOGO } from './SideNavigationMenuConfiguration';
import { withPermissions } from '../../../../helpers/withPermissionsHOC';
import { useSideNavigationMenuStyles } from './SideNavigationMenu.styles';
import { MENU_LINKS } from '../../../../config/routes';
import { Branding } from '../../../../models/Branding';
import { useSelector } from 'react-redux';
import { selectBranding } from '../../../../main/store/Branding/reducer';
import { CollapsibleMenu } from './CollapsibleMenu/CollapsibleMenu';
import { SideBarTemplate } from './SideBarTemplate/SideBarTemplate';
import { SENDBOARD_LOCATION_HASH } from '../../../constants/productsUrlHashes';
import { useMediaQuery } from '@material-ui/core';

export interface ISideNavigationMenuProps {
    currentHash: string;
    expand: boolean;
    menuConfig: ISideNavigationMenuItemsConfig[];
    isTrialActive: boolean;
    isCollapsible: boolean;
    handleOpenNewCertifiedFileModal: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    handleOpenNewRequestModal: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    handleOpenSendboardLeaveModal: (route: string) => void;
    handleDrawerToggle: () => void;
    permissions: string[];
}

const SideNavigationMenuComponent = ({
    currentHash,
    menuConfig,
    expand,
    isTrialActive,
    isCollapsible,
    handleOpenNewCertifiedFileModal,
    handleOpenNewRequestModal,
    handleOpenSendboardLeaveModal,
    handleDrawerToggle,
}: ISideNavigationMenuProps) => {
    const isDesktop = useMediaQuery('@media (min-width: 1192px)');

    const [collapsed, toggleCollapsed] = useState<boolean>(true);
    const [permanent, togglePermanent] = useState<boolean>(false);

    const isSendBoardMicro = currentHash.startsWith(SENDBOARD_LOCATION_HASH);

    useEffect(() => {
        toggleCollapsed(!isDesktop ? true : isCollapsible);
    }, [isCollapsible, expand, currentHash]);

    useEffect(() => {
        toggleCollapsed(!expand);
    }, [expand]);

    useEffect(() => {
        if (!isDesktop || isSendBoardMicro) {
            togglePermanent(false);
            toggleCollapsed(true);
        } else {
            togglePermanent(true);
            toggleCollapsed(false);
        }
    }, [isSendBoardMicro, isDesktop]);

    const [logoUrl, setLogoUrl] = useState<string>('');
    const branding: InstanceType<typeof Branding> | undefined = useSelector(selectBranding);

    useEffect(() => {
        const brandingBlueLogo = branding?.secondaryLogo;
        const signaturitBlueLogo = SIDE_MENU_URL_LOGO.BLUE_LINK;
        const srcLogo = brandingBlueLogo ?? signaturitBlueLogo;
        setLogoUrl(srcLogo);
    }, [branding]);

    const classes = useSideNavigationMenuStyles();
    const currentUrlHash = window.location.hash;

    const showCertificateButton =
        currentHash.startsWith(MENU_LINKS.FILES.BASE) ||
        (currentUrlHash && currentUrlHash.startsWith(MENU_LINKS.FILES.BASE));

    const showNewRequest = menuConfig.some(
        ({ baseLink, visible }) => baseLink === MENU_LINKS.REQUEST_LIST.BASE && visible
    );

    return (
        <nav className={classes.wrapper} aria-label="side-navigation-menu">
            <CollapsibleMenu
                collapsed={collapsed}
                permanent={permanent}
                toggleCollapsed={toggleCollapsed}
                handleDrawerToggle={handleDrawerToggle}
                isTrialActive={isTrialActive}
                togglePermanent={togglePermanent}
                isSendBoardMicro={isSendBoardMicro}
            >
                <SideBarTemplate
                    currentHash={currentHash}
                    handleOpenNewCertifiedFileModal={handleOpenNewCertifiedFileModal}
                    handleOpenNewRequestModal={handleOpenNewRequestModal}
                    handleOpenSendboardLeaveModal={handleOpenSendboardLeaveModal}
                    isCollapsible={isCollapsible}
                    logoUrl={logoUrl}
                    menuConfig={menuConfig}
                    showCertificateButton={showCertificateButton}
                    showNewRequest={showNewRequest}
                />
            </CollapsibleMenu>
        </nav>
    );
};

export const SideNavigationMenu = withPermissions(SideNavigationMenuComponent);
